/**
 * useMerchantReadySync Hook (MPRN-6)
 *
 * Global WebSocket subscription for the "your store is ready" notification the
 * provisioner emits (MPRN-4) on genuine readiness. Subscribes to
 * user.{userGuid}.notifications and toasts on the merchant-ready SYSTEM_NOTIFICATION.
 *
 * Why this exists: the creation wizard only polls while it's open (path A). For
 * waitlist activation (B) and admin approval (C) the owner has no open wizard when
 * provisioning later completes — polling never sees it. This owner-scoped realtime
 * event reaches them across tabs and devices (Risk 3). The email (MPRN-4) is the
 * durable fallback for owners who are offline.
 */
import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Anchor, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  useWebSocketOptional,
  useRealtimeEventOptional,
  EventType,
  RealtimeTopics,
} from '@teletienda/ws-client/react';
import { useAuth } from '@teletienda/shared-auth-ui';
import { useTranslation } from 'react-i18next';
import { useProvisioningWizardStore } from '@/stores';

/**
 * SYSTEM_NOTIFICATION is a generic event. The merchant-ready notification (MPRN-4)
 * carries a `merchantGuid` + `url` and is NOT an ops error alert (MPRN-5 ops alerts
 * set `severity: 'error'` and target admins, not the owner's topic).
 */
interface SystemNotificationPayload {
  title?: string;
  body?: string;
  url?: string;
  merchantGuid?: string;
  severity?: string;
}

export function useMerchantReadySync(): void {
  const { user, isAuthenticated } = useAuth();
  const { isConnected, subscribe, unsubscribe } = useWebSocketOptional();
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  // Per-merchant dedupe — guards reconnect replays and repeated emits (AC2).
  const toastedGuids = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isConnected || !isAuthenticated || !user?.userGuid) return;

    const topic = RealtimeTopics.userNotifications(user.userGuid);
    subscribe([topic]);

    return () => {
      unsubscribe([topic]);
    };
  }, [isConnected, isAuthenticated, user?.userGuid, subscribe, unsubscribe]);

  useRealtimeEventOptional<SystemNotificationPayload>(
    EventType.SYSTEM_NOTIFICATION,
    useCallback(
      (payload) => {
        const merchantGuid = payload.merchantGuid;
        // Only the owner-facing readiness notification (has a merchantGuid, not an
        // ops error alert).
        if (!merchantGuid || payload.severity === 'error') return;

        // MPF-1: refresh any list/card showing this owner's merchants so the
        // deploy→ready transition flips live. Done before the toast dedupe so a
        // replayed event still reconciles the UI (invalidate is idempotent).
        queryClient.invalidateQueries({ queryKey: ['my-merchants'] });

        // Dedupe: once per merchant, and skip when the creation wizard is actively
        // confirming this merchant inline (path A) — avoids a redundant toast (AC4).
        if (toastedGuids.current.has(merchantGuid)) return;
        if (useProvisioningWizardStore.getState().activeMerchantGuid === merchantGuid) return;
        toastedGuids.current.add(merchantGuid);

        const body = payload.body || t('merchant_ready_toast_message');

        notifications.show({
          title: payload.title || t('merchant_ready_toast_title'),
          color: 'green',
          autoClose: 8000,
          message: payload.url ? (
            <Text size="sm">
              {body}{' '}
              <Anchor href={payload.url} target="_blank" rel="noopener noreferrer">
                {t('merchant_ready_toast_link')}
              </Anchor>
            </Text>
          ) : (
            body
          ),
        });
      },
      [t, queryClient],
    ),
    [user?.userGuid],
  );
}
